import { apiJson, apiPost, jsonFromResponse, normalizeApiError } from "@/shared/api/client";
import {
  withActivitySource,
  getAutomaticActivitySource,
  normalizeActivitySource,
  type ActivityRequestInit,
  type ActivitySource,
} from "@/shared/api/activity-source";
import { shouldAttachAuthBearer } from "./auth-bearer-policy.mjs";
import {
  isValidRefreshRequestId,
  readInitialAuthenticatedTokenResponse,
  readRefreshAuthenticatedTokenResponse,
} from "./authenticated-token-response.mjs";
import { readClearedMeResponse } from "./cleared-me-response.mjs";
import {
  readPendingContactSignIn,
  type ContactVerificationDeliveryChannel,
  type PendingContactSignIn,
} from "./pending-contact-signin.mjs";
import { readPasswordResetConfirmation } from "./password-reset-link.mjs";
import {
  readPasswordChangeApprovalStatus,
  readPasswordChangeChallenge,
  readPasswordChangeConfirmation,
} from "./password-change-challenge.mjs";
import { readVerificationLinkPurpose } from "./verification-link-purpose.mjs";
import { createWebIdempotencyKey, getWebDeviceId } from "./web-device-id.mjs";
import { sessionNeedsServerContactClearance } from "./session-contact-clearance.mjs";
import {
  accountStorageScopeForStableId,
  ensurePendingAccountStorageBootstrap,
  purgeAccountStorageScope,
} from "@/shared/storage/account-storage-migration.mjs";
import {
  canConfirmAccountDeletion,
  readAccountDeletionResponse,
  type AccountDeletionResponse,
} from "./account-deletion-policy.mjs";
import { hasValidPublicUserIdentity, publicUserId } from "./public-user-identity.mjs";
import {
  createAuthSessionMutationFence,
  createSingleFlightByKey,
  type AuthSessionCredentialPermit,
  type AuthSessionStatePermit,
  type AuthSessionTransitionPermit,
} from "./session-mutation-fence.mjs";
import { isTransientRefreshFailure } from "./refresh-failure-policy.mjs";

export {
  isLinkApprovalContactSignIn,
  readPendingContactSignIn,
  type ContactIdentityType,
  type ContactVerificationDeliveryChannel,
  type PendingContactSignIn,
} from "./pending-contact-signin.mjs";
export {
  readContactSignInLinkApproval,
  readPasswordChangeLinkApproval,
  readPendingContactSignInStatus,
  type ContactSignInLinkApproval,
  type PasswordChangeLinkApproval,
  type PendingContactSignInStatus,
} from "./contact-sign-in-relay.mjs";
export {
  readVerificationLinkPurpose,
  type VerificationLinkPurpose,
} from "./verification-link-purpose.mjs";
export {
  expectedPasswordChangeDelivery,
  type ExpectedPasswordChangeDelivery,
  type PasswordChangeApprovalStatus,
  type PasswordChangeChallenge,
  type PasswordChangeCompletionMode,
  type PasswordChangeDeliveryChannel,
  type PasswordChangeDeliveryStatus,
  type PasswordChangeIdentityType,
} from "./password-change-challenge.mjs";
export { createWebRegistrationAttemptId, getWebDeviceId } from "./web-device-id.mjs";
export type { AuthSessionTransitionPermit } from "./session-mutation-fence.mjs";

const SESSION_KEY = "tb_auth_session_v1";
const TB_CLIENT = "web";
const CURRENT_WEB_AUDIO_CONTRACT = "2";
const REFRESH_RETRY_DELAY_MS = 15_000;
const REFRESH_REQUEST_TIMEOUT_MS = 20_000;
export const CONTACT_VERIFICATION_REQUIRED_EVENT = "tb-contact-verification-required";
export const CONTACT_VERIFICATION_VALIDATED_EVENT = "tb-contact-verification-validated";

function officialWebRequestHeaders(input?: HeadersInit): Headers {
  const headers = new Headers(input);
  headers.set("x-tb-client", TB_CLIENT);
  headers.set("x-tb-web-audio-contract", CURRENT_WEB_AUDIO_CONTRACT);
  headers.set("Accept", "application/json");
  return headers;
}

export type AuthUser = {
  userId?: string;
  id?: string;
  email?: string | null;
  emailVerified?: boolean;
  phone?: string | null;
  phoneE164?: string | null;
  phoneVerified?: boolean;
  verificationIdentityType: "email" | "phone" | null;
  hasPasskey?: boolean;
  displayName?: string | null;
  subscriptionTier?: string | null;
};

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  refreshRequestId: string;
  refreshExpiresAt: string;
  sessionId: string;
  accessExpiresAt: number;
  user: AuthUser | null;
  rememberMe?: boolean;
  contactVerificationDeviceCleared: boolean;
  localDataBootstrapRequired?: boolean;
  localDataBootstrapAuthorityAudited?: boolean;
};

export type AuthTokenResponse = {
  ok?: boolean;
  accountCreated?: boolean;
  localDataBootstrapRequired?: boolean;
  accessToken?: string;
  refreshToken?: string;
  refreshAttemptId?: string;
  refreshRequestId?: string;
  refreshExpiresAt?: string;
  sessionId?: string;
  accessExpiresIn?: number;
  contactVerificationDeviceCleared?: boolean;
  user?: AuthUser | null;
};

export type AuthLoginResponse = AuthTokenResponse | PendingContactSignIn;

export type AuthOtpResponse = {
  ok?: boolean;
  expiresInSec?: number;
  reissued?: boolean;
  identityType?: "email" | "phone";
  deliveryChannel?: string | null;
  primaryDeliveryChannel?: string | null;
  fallbackDeliveryChannel?: string | null;
  whatsappFallbackAvailable?: boolean;
  whatsappFallbackEngaged?: boolean;
  whatsappFallbackAutomatic?: boolean;
  voiceFallbackAvailable?: boolean;
  voiceFallbackEngaged?: boolean;
  whatsappVerificationMode?: string;
};

export type AuthVerifyResponse = AuthTokenResponse & {
  verified?: boolean;
  purpose?: "verification" | "identity_change" | "contact_sign_in" | "reset" | "password_change";
  passwordResetRequired?: boolean;
  user?: AuthUser | null;
};

export type AuthSessionInfo = {
  sessionId?: string;
  deviceId?: string | null;
  userAgent?: string | null;
  ip?: string | null;
  createdAt?: string;
  lastSeenAt?: string;
  expiresAt?: string;
  current?: boolean;
  trusted?: boolean;
  rememberMe?: boolean;
};

export type AuthSessionOwner = {
  sessionId: string;
  userId: string;
};

let memorySession: AuthSession | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
const sessionMutationFence = createAuthSessionMutationFence();
const refreshSingleFlight = createSingleFlightByKey();
const responseStatePermits = new WeakMap<Response, AuthSessionStatePermit>();

function sessionCredentialIdentity(session: AuthSession | null): string {
  if (!session) return "";
  const userId = String(session.user?.userId || session.user?.id || "");
  return ["active", session.sessionId.length, session.sessionId, userId.length, userId].join(":");
}

function requestCredentialOwnerIsCurrent(
  permit: AuthSessionCredentialPermit,
  session: AuthSession | null,
): session is AuthSession {
  const currentIdentity = sessionCredentialIdentity(session);
  return Boolean(currentIdentity && permit.identity === currentIdentity);
}

function synchronizeSessionFence(session: AuthSession | null) {
  sessionMutationFence.synchronizeCredentials(sessionCredentialIdentity(session));
}

function mutationSupersededError() {
  const error = new Error("auth_session_mutation_superseded") as Error & {
    code: string;
  };
  error.code = "auth_session_mutation_superseded";
  return error;
}

function requestAbortReason(signal: AbortSignal): unknown {
  if (signal.reason !== undefined) return signal.reason;
  const error = new Error("request_aborted") as Error & { code: string };
  error.name = "AbortError";
  error.code = "request_aborted";
  return error;
}

function throwIfRequestAborted(signal: AbortSignal | null | undefined): void {
  if (signal?.aborted) throw requestAbortReason(signal);
}

function waitForRequestAbort<T>(
  promise: Promise<T>,
  signal: AbortSignal | null | undefined,
): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(requestAbortReason(signal));
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", onAbort);
      callback();
    };
    const onAbort = () => finish(() => reject(requestAbortReason(signal)));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => finish(() => resolve(value)),
      (error) => finish(() => reject(error)),
    );
  });
}

function refreshRequestTimeoutError() {
  const error = new Error("refresh_request_timeout") as Error & {
    code: string;
    status: number;
  };
  error.name = "RefreshRequestTimeoutError";
  error.code = "refresh_request_timeout";
  error.status = 408;
  return error;
}

async function runRefreshRequestWithDeadline<T>(
  read: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timedOut = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      const error = refreshRequestTimeoutError();
      controller.abort(error);
      reject(error);
    }, REFRESH_REQUEST_TIMEOUT_MS);
  });
  try {
    return await Promise.race([Promise.resolve().then(() => read(controller.signal)), timedOut]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

function isMutationSupersededError(err: unknown): boolean {
  return (
    (err as { code?: unknown } | null)?.code === "auth_session_mutation_superseded" ||
    (err as { message?: unknown } | null)?.message === "auth_session_mutation_superseded"
  );
}

function storageForRemember(rememberMe: boolean) {
  return rememberMe ? localStorage : sessionStorage;
}

function availableBrowserStorages(): Storage[] {
  const stores: Storage[] = [];
  for (const key of ["localStorage", "sessionStorage"] as const) {
    try {
      const store = globalThis[key];
      if (store) stores.push(store);
    } catch {
      // Storage access can be denied independently in hardened/private contexts.
    }
  }
  return stores;
}

function readStoredSession(): AuthSession | null {
  for (const store of availableBrowserStorages()) {
    try {
      const raw = store.getItem(SESSION_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as AuthSession;
      const refreshExpiry = Date.parse(String(parsed?.refreshExpiresAt || ""));
      const valid =
        typeof parsed?.accessToken === "string" &&
        Boolean(parsed.accessToken.trim()) &&
        typeof parsed.refreshToken === "string" &&
        Boolean(parsed.refreshToken.trim()) &&
        (!Object.prototype.hasOwnProperty.call(parsed, "refreshRequestId") ||
          isValidRefreshRequestId(parsed.refreshRequestId)) &&
        typeof parsed.sessionId === "string" &&
        Boolean(parsed.sessionId.trim()) &&
        Number.isFinite(parsed.accessExpiresAt) &&
        parsed.accessExpiresAt > 0 &&
        Number.isFinite(refreshExpiry) &&
        refreshExpiry > Date.now() &&
        parsed.contactVerificationDeviceCleared === true &&
        (!Object.prototype.hasOwnProperty.call(parsed, "localDataBootstrapAuthorityAudited") ||
          typeof parsed.localDataBootstrapAuthorityAudited === "boolean") &&
        hasValidPublicUserIdentity(parsed.user);
      if (valid) return parsed;
      store.removeItem(SESSION_KEY);
    } catch {
      try {
        store.removeItem(SESSION_KEY);
      } catch {
        /* ignore */
      }
    }
  }
  return null;
}

function writeStoredSession(session: AuthSession, rememberMe: boolean) {
  const target = storageForRemember(rememberMe);
  const other = rememberMe ? sessionStorage : localStorage;
  const previousTarget = target.getItem(SESSION_KEY);
  target.setItem(SESSION_KEY, JSON.stringify(session));
  try {
    other.removeItem(SESSION_KEY);
  } catch (error) {
    try {
      if (previousTarget == null) target.removeItem(SESSION_KEY);
      else target.setItem(SESSION_KEY, previousTarget);
    } catch {
      /* retain the in-memory pre-mutation session and surface the write failure */
    }
    throw error;
  }
}

function clearStoredSession() {
  for (const store of availableBrowserStorages()) {
    try {
      store.removeItem(SESSION_KEY);
    } catch {
      // Memory auth must still clear after logout or permanent account deletion.
    }
  }
}

function dispatchAuthChanged() {
  window.dispatchEvent(new CustomEvent("tb-auth-changed", { detail: { user: getCurrentUser() } }));
}

function dispatchContactVerificationRequired() {
  window.dispatchEvent(new CustomEvent(CONTACT_VERIFICATION_REQUIRED_EVENT));
}

function dispatchContactVerificationValidated() {
  window.dispatchEvent(new CustomEvent(CONTACT_VERIFICATION_VALIDATED_EVENT));
}

function markCurrentSessionContactClearance(cleared: boolean, permit: AuthSessionStatePermit) {
  if (!sessionMutationFence.isStateCurrent(permit)) return false;
  const session = getAuthSession();
  if (!session) return false;
  if (session.contactVerificationDeviceCleared === cleared) return true;
  const next = { ...session, contactVerificationDeviceCleared: cleared };
  if (!sessionMutationFence.isStateCurrent(permit)) return false;
  writeStoredSession(next, next.rememberMe !== false);
  memorySession = next;
  sessionMutationFence.noteStateChanged();
  return true;
}

function isContactVerificationRequiredError(err: unknown): boolean {
  return normalizeApiError(err).code === "contact_verification_required";
}

async function signalContactVerificationRequiredResponse(
  res: Response,
  permit: AuthSessionStatePermit,
) {
  if (res.status !== 403) return;
  const body = (await res
    .clone()
    .json()
    .catch(() => null)) as { error?: unknown; code?: unknown } | null;
  if (
    (body?.error === "contact_verification_required" ||
      body?.code === "contact_verification_required") &&
    markCurrentSessionContactClearance(false, permit)
  ) {
    dispatchContactVerificationRequired();
  }
}

export function getAuthSession(): AuthSession | null {
  if (memorySession?.accessToken) {
    synchronizeSessionFence(memorySession);
    return memorySession;
  }
  const stored = readStoredSession();
  if (stored) memorySession = stored;
  synchronizeSessionFence(memorySession);
  return memorySession;
}

export function getCurrentUser(): AuthUser | null {
  const session = getAuthSession();
  return session?.contactVerificationDeviceCleared === true &&
    hasValidPublicUserIdentity(session.user)
    ? session.user
    : null;
}

export function captureAuthSessionOwner(): AuthSessionOwner | null {
  const session = getAuthSession();
  const userId = String(session?.user?.userId || session?.user?.id || "").trim();
  const sessionId = String(session?.sessionId || "").trim();
  if (!sessionId || !userId) return null;
  return Object.freeze({ sessionId, userId });
}

export function isAuthSessionOwnerCurrent(owner: AuthSessionOwner): boolean {
  const current = captureAuthSessionOwner();
  return Boolean(
    current && current.sessionId === owner.sessionId && current.userId === owner.userId,
  );
}

export function isSignedIn(): boolean {
  const session = getAuthSession();
  return Boolean(
    session?.accessToken &&
    session.contactVerificationDeviceCleared === true &&
    publicUserId(session.user) &&
    hasValidPublicUserIdentity(session.user),
  );
}

export function beginAuthSessionMutation(label: string): AuthSessionTransitionPermit {
  return sessionMutationFence.beginTransition(label);
}

export function isAuthSessionMutationCurrent(permit: AuthSessionTransitionPermit): boolean {
  return sessionMutationFence.isTransitionCurrent(permit);
}

export function cancelAuthSessionMutation(permit: AuthSessionTransitionPermit): boolean {
  return sessionMutationFence.retireTransition(permit);
}

function applyAuthTokens(
  tokens: {
    accessToken: string;
    refreshToken: string;
    refreshRequestId?: string;
    refreshExpiresAt: string;
    sessionId: string;
    accessExpiresIn: number;
    contactVerificationDeviceCleared: true;
    localDataBootstrapRequired?: boolean;
    user?: AuthUser | null;
  },
  permit: AuthSessionTransitionPermit,
  opts: { rememberMe?: boolean } = {},
) {
  if (!sessionMutationFence.isTransitionCurrent(permit)) return null;
  const rememberMe = opts.rememberMe !== false;
  const accessExpiresIn = tokens.accessExpiresIn;
  const refreshRequestId = tokens.refreshRequestId ?? createWebIdempotencyKey();
  const session: AuthSession = {
    accessToken: String(tokens.accessToken),
    refreshToken: String(tokens.refreshToken),
    refreshRequestId,
    refreshExpiresAt: tokens.refreshExpiresAt,
    sessionId: tokens.sessionId,
    accessExpiresAt: Date.now() + accessExpiresIn * 1000 - 30_000,
    user: tokens.user ?? null,
    rememberMe,
    contactVerificationDeviceCleared: tokens.contactVerificationDeviceCleared,
    localDataBootstrapRequired: tokens.localDataBootstrapRequired === true,
    // A valid live token envelope is authoritative for this exact session:
    // current servers send `true` while bootstrap is owed, and false/absence
    // means there is no creating-device fence to recover.
    localDataBootstrapAuthorityAudited: true,
  };
  if (!sessionMutationFence.isTransitionCurrent(permit)) return null;
  writeStoredSession(session, rememberMe);
  memorySession = session;
  sessionMutationFence.noteCredentialsChanged(sessionCredentialIdentity(session));
  scheduleSilentRefresh();
  dispatchAuthChanged();
  return session;
}

export function applyAuthTokenResponse(
  tokens: AuthTokenResponse,
  permit: AuthSessionTransitionPermit,
  opts: { rememberMe?: boolean } = {},
) {
  if (!sessionMutationFence.isTransitionCurrent(permit)) return null;
  const authenticated = readInitialAuthenticatedTokenResponse(tokens);
  if (!authenticated) return null;
  return applyAuthTokens(
    {
      accessToken: String(authenticated.accessToken),
      refreshToken: String(authenticated.refreshToken),
      refreshRequestId: authenticated.refreshRequestId,
      refreshExpiresAt: authenticated.refreshExpiresAt,
      sessionId: authenticated.sessionId,
      accessExpiresIn: authenticated.accessExpiresIn,
      contactVerificationDeviceCleared: true,
      localDataBootstrapRequired: authenticated.localDataBootstrapRequired === true,
      user: authenticated.user as AuthUser,
    },
    permit,
    opts,
  );
}

export function clearAuthSession() {
  sessionMutationFence.cancelAllTransitions();
  clearAuthSessionStorage();
}

export function reconcileAuthSessionFromStorage(): AuthSession | null {
  // A same-origin tab may replace or clear a remembered session. Fence every
  // in-flight response from the previous credentials before adopting storage.
  sessionMutationFence.cancelAllTransitions();
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  memorySession = null;
  const stored = readStoredSession();
  memorySession = stored;
  sessionMutationFence.noteCredentialsChanged(sessionCredentialIdentity(stored));
  if (stored) scheduleSilentRefresh();
  return stored;
}

function clearAuthSessionStorage() {
  memorySession = null;
  clearStoredSession();
  sessionMutationFence.noteCredentialsChanged("");
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  dispatchAuthChanged();
}

function clearAuthSessionIfCurrent(permit: AuthSessionCredentialPermit): boolean {
  if (!sessionMutationFence.isCredentialCurrent(permit)) return false;
  clearAuthSessionStorage();
  return true;
}

function clearAuthSessionIfStateCurrent(permit: AuthSessionStatePermit): boolean {
  if (!sessionMutationFence.isStateCurrent(permit)) return false;
  clearAuthSessionStorage();
  return true;
}

function scheduleSilentRefresh(delayOverrideMs?: number) {
  if (refreshTimer) clearTimeout(refreshTimer);
  const s = getAuthSession();
  if (!s?.refreshToken) return;
  const permit = sessionMutationFence.captureCredential();
  const delay =
    typeof delayOverrideMs === "number" && Number.isFinite(delayOverrideMs)
      ? Math.max(5_000, delayOverrideMs)
      : Math.max(5_000, (s.accessExpiresAt || 0) - Date.now());
  refreshTimer = setTimeout(() => {
    void refreshAccessToken(permit, "background").catch((err) => {
      if (isContactVerificationRequiredError(err) || isMutationSupersededError(err)) return;
      if (isTransientRefreshFailure(err)) {
        scheduleRefreshRetryIfCurrent(permit);
        return;
      }
      clearAuthSessionIfCurrent(permit);
    });
  }, delay);
}

function scheduleRefreshRetryIfCurrent(permit: AuthSessionCredentialPermit): boolean {
  if (!sessionMutationFence.isCredentialCurrent(permit)) return false;
  scheduleSilentRefresh(REFRESH_RETRY_DELAY_MS);
  return true;
}

function ensureDurableRefreshRequestId(
  session: AuthSession,
  permit: AuthSessionCredentialPermit,
  identity: string,
): AuthSession {
  if (isValidRefreshRequestId(session.refreshRequestId)) return session;
  if (
    Object.prototype.hasOwnProperty.call(session, "refreshRequestId") ||
    !sessionMutationFence.isCredentialCurrent(permit) ||
    sessionCredentialIdentity(session) !== identity
  ) {
    throw normalizeApiError(
      new Error("invalid_auth_session_response"),
      "invalid_auth_session_response",
    );
  }

  // Staged sessions created before the server issued request ids get exactly
  // one local id. Persist it before any refresh bytes leave the browser so a
  // timeout, interrupted body, or hard navigation can replay the same attempt.
  const next = {
    ...session,
    refreshRequestId: createWebIdempotencyKey(),
  };
  if (!sessionMutationFence.isCredentialCurrent(permit)) {
    throw mutationSupersededError();
  }
  writeStoredSession(next, next.rememberMe !== false);
  memorySession = next;
  return next;
}

export async function refreshAccessToken(
  suppliedPermit?: AuthSessionCredentialPermit,
  activitySource: ActivitySource = "unknown",
): Promise<AuthSession> {
  const s = getAuthSession();
  if (!s?.refreshToken) throw new Error("no_refresh");
  const permit = suppliedPermit ?? sessionMutationFence.captureCredential();
  if (!sessionMutationFence.isCredentialCurrent(permit)) {
    throw mutationSupersededError();
  }
  const identity = sessionCredentialIdentity(s);
  const singleFlightKey = `${permit.credentialEpoch}:${identity}`;

  return refreshSingleFlight.run(singleFlightKey, async () => {
    if (!sessionMutationFence.isCredentialCurrent(permit)) {
      throw mutationSupersededError();
    }
    const currentRefreshSession = getAuthSession();
    if (!currentRefreshSession || sessionCredentialIdentity(currentRefreshSession) !== identity) {
      throw mutationSupersededError();
    }
    const refreshSession = ensureDurableRefreshRequestId(currentRefreshSession, permit, identity);
    if (!refreshSession || sessionCredentialIdentity(refreshSession) !== identity) {
      throw mutationSupersededError();
    }
    const statePermit = sessionMutationFence.captureState();
    let data: AuthTokenResponse;
    try {
      data = await runRefreshRequestWithDeadline((signal) =>
        apiJson<AuthTokenResponse>(
          "/api/auth/token/refresh",
          {
            method: "POST",
            activitySource,
            headers: { "Idempotency-Key": refreshSession.refreshRequestId },
            body: JSON.stringify({
              refreshToken: refreshSession.refreshToken,
              refreshRequestId: refreshSession.refreshRequestId,
            }),
            signal,
          },
          { fallbackCode: "refresh_failed" },
        ),
      );
    } catch (err) {
      if (
        isContactVerificationRequiredError(err) &&
        markCurrentSessionContactClearance(false, statePermit)
      ) {
        dispatchContactVerificationRequired();
      }
      throw err;
    }
    const authenticated = readRefreshAuthenticatedTokenResponse(
      data,
      refreshSession.refreshRequestId,
    );
    if (
      !sessionMutationFence.isCredentialCurrent(permit) ||
      !sessionMutationFence.isStateCurrent(statePermit)
    ) {
      throw mutationSupersededError();
    }
    const current = getAuthSession();
    if (!current || sessionCredentialIdentity(current) !== identity) {
      throw mutationSupersededError();
    }
    if (authenticated.sessionId !== current.sessionId) {
      throw normalizeApiError(
        new Error("invalid_auth_token_response"),
        "invalid_auth_token_response",
      );
    }
    const next: AuthSession = {
      ...current,
      accessToken: authenticated.accessToken,
      refreshToken: authenticated.refreshToken,
      refreshRequestId: authenticated.refreshRequestId,
      refreshExpiresAt: authenticated.refreshExpiresAt,
      sessionId: authenticated.sessionId,
      accessExpiresAt: Date.now() + authenticated.accessExpiresIn * 1000 - 30_000,
      contactVerificationDeviceCleared: true,
      localDataBootstrapRequired: authenticated.localDataBootstrapRequired === true,
      localDataBootstrapAuthorityAudited: true,
    };
    if (
      !sessionMutationFence.isCredentialCurrent(permit) ||
      !sessionMutationFence.isStateCurrent(statePermit)
    ) {
      throw mutationSupersededError();
    }
    writeStoredSession(next, next.rememberMe !== false);
    memorySession = next;
    sessionMutationFence.noteCredentialsChanged(sessionCredentialIdentity(next));
    scheduleSilentRefresh();
    dispatchAuthChanged();
    dispatchContactVerificationValidated();
    return next;
  });
}

export async function restoreAuthSession() {
  const s = getAuthSession();
  if (!s?.refreshToken) return null;
  const permit = sessionMutationFence.captureCredential();
  if (sessionNeedsServerContactClearance(s)) {
    dispatchContactVerificationRequired();
  }
  if (s.accessExpiresAt && s.accessExpiresAt > Date.now()) {
    scheduleSilentRefresh();
    dispatchAuthChanged();
    return s;
  }
  try {
    return await refreshAccessToken(permit, getAutomaticActivitySource());
  } catch (err) {
    if (isContactVerificationRequiredError(err)) return null;
    if (isMutationSupersededError(err)) return getAuthSession();
    if (isTransientRefreshFailure(err)) {
      scheduleRefreshRetryIfCurrent(permit);
      return getAuthSession();
    }
    clearAuthSessionIfCurrent(permit);
    return getAuthSession();
  }
}

export async function authFetch(path: string, init: ActivityRequestInit = {}) {
  const activitySource = normalizeActivitySource(init.activitySource);
  const requestInit = withActivitySource(path, { ...init, activitySource });
  const headers = officialWebRequestHeaders(requestInit.headers);
  const session = getAuthSession();
  const token = session?.accessToken;
  let requestCredentialPermit: AuthSessionCredentialPermit | null = null;
  let requestStatePermit: AuthSessionStatePermit | null = null;
  if (token && shouldAttachAuthBearer(path, session)) {
    headers.set("Authorization", `Bearer ${token}`);
    requestCredentialPermit = sessionMutationFence.captureCredential();
    requestStatePermit = sessionMutationFence.captureState();
  }
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  let res = await waitForRequestAbort(
    fetch(path, { ...requestInit, headers, credentials: "same-origin" }),
    init.signal,
  );
  if (requestStatePermit) {
    responseStatePermits.set(res, requestStatePermit);
    await signalContactVerificationRequiredResponse(res, requestStatePermit);
  }
  if (res.status === 401 && requestCredentialPermit) {
    const recoverySession = getAuthSession();
    if (
      !requestCredentialOwnerIsCurrent(requestCredentialPermit, recoverySession) ||
      !recoverySession.refreshToken ||
      !shouldAttachAuthBearer(path, recoverySession)
    ) {
      return res;
    }

    let refreshed = recoverySession;
    if (
      recoverySession.accessToken === token ||
      !Number.isFinite(recoverySession.accessExpiresAt) ||
      recoverySession.accessExpiresAt <= Date.now()
    ) {
      const recoveryPermit = sessionMutationFence.captureCredential();
      try {
        refreshed = await waitForRequestAbort(
          refreshAccessToken(recoveryPermit, activitySource),
          init.signal,
        );
      } catch (err) {
        throwIfRequestAborted(init.signal);
        if (!isContactVerificationRequiredError(err) && !isMutationSupersededError(err)) {
          if (isTransientRefreshFailure(err)) {
            scheduleRefreshRetryIfCurrent(recoveryPermit);
          } else {
            clearAuthSessionIfCurrent(recoveryPermit);
          }
        }
        return res;
      }
    }

    const retryCredentialPermit = sessionMutationFence.captureCredential();
    const retryStatePermit = sessionMutationFence.captureState();
    const retrySession = getAuthSession();
    if (
      !retrySession ||
      sessionCredentialIdentity(retrySession) !== sessionCredentialIdentity(refreshed) ||
      !requestCredentialOwnerIsCurrent(requestCredentialPermit, retrySession)
    ) {
      return res;
    }
    if (
      !sessionMutationFence.isCredentialCurrent(retryCredentialPermit) ||
      !shouldAttachAuthBearer(path, retrySession)
    ) {
      return res;
    }
    throwIfRequestAborted(init.signal);
    const retryHeaders = officialWebRequestHeaders(requestInit.headers);
    retryHeaders.set("Authorization", `Bearer ${retrySession.accessToken}`);
    if (init.body && !retryHeaders.has("Content-Type")) {
      retryHeaders.set("Content-Type", "application/json");
    }
    if (!sessionMutationFence.isCredentialCurrent(retryCredentialPermit)) {
      return res;
    }
    res = await waitForRequestAbort(
      fetch(path, {
        ...requestInit,
        headers: retryHeaders,
        credentials: "same-origin",
      }),
      init.signal,
    );
    responseStatePermits.set(res, retryStatePermit);
    await signalContactVerificationRequiredResponse(res, retryStatePermit);
    if (!sessionMutationFence.isCredentialCurrent(retryCredentialPermit)) {
      responseStatePermits.delete(res);
    }
  }
  return res;
}

export async function authGet<T = Record<string, unknown>>(
  path: string,
  init: ActivityRequestInit = {},
) {
  const res = await authFetch(path, init);
  return jsonFromResponse<T>(res, { fallbackCode: "request_failed" });
}

export async function authPost<T = Record<string, unknown>>(
  path: string,
  body: Record<string, unknown> = {},
  options: { activitySource?: ActivitySource } = {},
): Promise<T> {
  const res = await authFetch(path, {
    method: "POST",
    activitySource: options.activitySource,
    body: JSON.stringify({ ...body, deviceId: body.deviceId ?? getWebDeviceId() }),
  });
  return jsonFromResponse<T>(res, { fallbackCode: "request_failed" });
}

export async function fetchMe(
  options: {
    expectedVerificationIdentityType?: AuthUser["verificationIdentityType"];
    signal?: AbortSignal;
    activitySource?: ActivitySource;
  } = {},
) {
  const res = await authFetch("/api/auth/me", {
    signal: options.signal,
    activitySource: options.activitySource,
  });
  const permit = responseStatePermits.get(res);
  if (!permit || !sessionMutationFence.isStateCurrent(permit)) {
    throw mutationSupersededError();
  }
  const data = await jsonFromResponse<unknown>(res, {
    fallbackCode: "me_failed",
  });
  if (!sessionMutationFence.isStateCurrent(permit)) {
    throw mutationSupersededError();
  }
  const s = getAuthSession();
  if (!s || !sessionMutationFence.isStateCurrent(permit)) {
    throw mutationSupersededError();
  }
  const currentUserId = String(s.user?.userId || s.user?.id || "");
  let cleared;
  try {
    cleared = readClearedMeResponse(data, currentUserId);
    if (
      options.expectedVerificationIdentityType !== undefined &&
      cleared.user.verificationIdentityType !== options.expectedVerificationIdentityType
    ) {
      const error = new Error("invalid_auth_session_response") as Error & { code: string };
      error.code = "invalid_auth_session_response";
      throw error;
    }
  } catch (err) {
    clearAuthSessionIfStateCurrent(permit);
    throw normalizeApiError(err, "invalid_auth_session_response");
  }
  if (cleared.localDataBootstrapRequired) {
    const targetScope = accountStorageScopeForStableId(currentUserId);
    try {
      ensurePendingAccountStorageBootstrap(
        typeof window === "undefined" ? null : window.localStorage,
        targetScope || "",
      );
    } catch {
      // Keep the valid authenticated session. Its durable bootstrap-required
      // bit prevents cloud pull until local staging can be retried.
    }
  }
  const next: AuthSession = {
    ...s,
    user: cleared.user as AuthUser,
    contactVerificationDeviceCleared: true,
    localDataBootstrapRequired: cleared.localDataBootstrapRequired,
    localDataBootstrapAuthorityAudited: true,
  };
  writeStoredSession(next, next.rememberMe !== false);
  memorySession = next;
  sessionMutationFence.noteStateChanged();
  dispatchAuthChanged();
  dispatchContactVerificationValidated();
  return next.user;
}

export function isCurrentSessionLocalDataBootstrapRequired(): boolean {
  return getAuthSession()?.localDataBootstrapRequired === true;
}

export function isCurrentSessionLocalDataBootstrapAuthorityAudited(): boolean {
  return getAuthSession()?.localDataBootstrapAuthorityAudited === true;
}

export function markCurrentSessionLocalDataBootstrapComplete(owner: AuthSessionOwner): boolean {
  if (!isAuthSessionOwnerCurrent(owner)) return false;
  const current = getAuthSession();
  if (!current) return false;
  if (current.localDataBootstrapRequired !== true) return true;
  const next: AuthSession = {
    ...current,
    localDataBootstrapRequired: false,
  };
  try {
    writeStoredSession(next, next.rememberMe !== false);
  } catch {
    return false;
  }
  memorySession = next;
  sessionMutationFence.noteStateChanged();
  dispatchAuthChanged();
  return true;
}

export function registerWithEmail(body: {
  email: string;
  password: string;
  displayName?: string;
  rememberMe?: boolean;
  forceDeviceRebind?: boolean;
  registrationAttemptId: string;
  language?: string;
}) {
  return authPost<AuthTokenResponse>("/api/auth/register/email", body, {
    activitySource: "user_initiated",
  });
}

export function registerWithPhone(body: {
  phone: string;
  password: string;
  displayName?: string;
  rememberMe?: boolean;
  defaultCountry?: string;
  forceDeviceRebind?: boolean;
  registrationAttemptId: string;
  language?: string;
}) {
  return authPost<AuthTokenResponse>("/api/auth/register/phone", body, {
    activitySource: "user_initiated",
  });
}

export function loginWithPassword(body: {
  email?: string;
  phone?: string;
  password: string;
  rememberMe?: boolean;
  trusted?: boolean;
  forceDeviceRebind?: boolean;
  defaultCountry?: string;
  language?: string;
}) {
  return authPost<AuthLoginResponse>("/api/auth/login/password", body, {
    activitySource: "user_initiated",
  });
}

export function loginWithGoogle(body: {
  idToken: string;
  rememberMe?: boolean;
  trusted?: boolean;
  accountCreationOnly?: boolean;
  forceDeviceRebind?: boolean;
  registrationAttemptId?: string;
  language?: string;
}) {
  return authPost<AuthLoginResponse>("/api/auth/login/google", body, {
    activitySource: "user_initiated",
  });
}

export function verifyPendingContactSignIn(body: {
  challengeId: string;
  completionSecret: string;
  code: string;
}) {
  return authPost<AuthTokenResponse>("/api/auth/login/contact/verify", body, {
    activitySource: "user_initiated",
  });
}

export async function resendPendingContactSignIn(body: {
  challengeId: string;
  completionSecret: string;
  deliveryChannel?: ContactVerificationDeliveryChannel;
  language?: string;
}) {
  const response = await authPost<unknown>("/api/auth/login/contact/resend", body, {
    activitySource: "user_initiated",
  });
  const pending = readPendingContactSignIn(response);
  if (
    pending &&
    pending.challengeId !== body.challengeId &&
    pending.completionSecret !== body.completionSecret
  ) {
    return pending;
  }
  const error = new Error("invalid_contact_sign_in_response") as Error & {
    code: string;
  };
  error.code = "invalid_contact_sign_in_response";
  throw error;
}

export function pollPendingContactSignIn(body: { challengeId: string; completionSecret: string }) {
  return authPost<unknown>("/api/auth/login/contact/status", body, {
    activitySource: getAutomaticActivitySource(),
  });
}

export function sendOtp(
  body: {
    email?: string;
    phone?: string;
    language?: string;
    channel?: string;
    deliveryChannel?: string;
    resend?: boolean;
    forceReissue?: boolean;
    purpose?: "verification" | "reset";
    defaultCountry?: string;
  },
  options: { idempotencyKey?: string; signal?: AbortSignal; activitySource?: ActivitySource } = {},
) {
  const headers = options.idempotencyKey
    ? { "Idempotency-Key": options.idempotencyKey }
    : undefined;
  return authFetch("/api/auth/otp/send", {
    activitySource: options.activitySource,
    method: "POST",
    headers,
    signal: options.signal,
    body: JSON.stringify({ ...body, deviceId: getWebDeviceId() }),
  }).then((response) =>
    jsonFromResponse<AuthOtpResponse>(response, { fallbackCode: "request_failed" }),
  );
}

export function verifyOtp(
  body: {
    email?: string;
    phone?: string;
    code: string;
    purpose?: "verification" | "reset";
    defaultCountry?: string;
  },
  options: { signal?: AbortSignal } = {},
) {
  return authFetch("/api/auth/otp/verify", {
    activitySource: "user_initiated",
    method: "POST",
    signal: options.signal,
    body: JSON.stringify({ ...body, deviceId: getWebDeviceId() }),
  }).then((response) =>
    jsonFromResponse<AuthVerifyResponse>(response, { fallbackCode: "request_failed" }),
  );
}

export function verifyOtpLink(body: { token: string; rememberMe?: boolean }) {
  const purpose = readVerificationLinkPurpose(body.token);
  if (purpose === "reset") {
    return apiPost<AuthVerifyResponse>(
      "/api/auth/password/reset/verify-link",
      { token: body.token },
      { fallbackCode: "verify_link_failed" },
    );
  }
  if (purpose === "contact_sign_in") {
    return apiPost<AuthVerifyResponse>(
      "/api/auth/otp/verify-link",
      { token: body.token },
      { fallbackCode: "verify_link_failed" },
    );
  }
  if (purpose === "password_change") {
    return apiPost<AuthVerifyResponse>(
      "/api/auth/otp/verify-link",
      { token: body.token },
      { fallbackCode: "verify_link_failed" },
    );
  }
  return authPost<AuthVerifyResponse>("/api/auth/otp/verify-link", body);
}

export function requestPasswordReset(body: {
  email?: string;
  phone?: string;
  language?: string;
  deliveryChannel?: string;
  resend?: boolean;
  forceReissue?: boolean;
  defaultCountry?: string;
}) {
  return authPost<AuthOtpResponse>("/api/auth/password/reset/request", body, {
    activitySource: "user_initiated",
  });
}

export async function confirmPasswordReset(
  body:
    | {
        resetToken: string;
        newPassword: string;
      }
    | {
        email?: string;
        phone?: string;
        code: string;
        newPassword: string;
        defaultCountry?: string;
      },
) {
  const response = await authPost<unknown>(
    "/api/auth/password/reset/confirm",
    { ...body },
    { activitySource: "user_initiated" },
  );
  return readPasswordResetConfirmation(response);
}

export function requestIdentityChange(body: {
  email?: string;
  phone?: string;
  language?: string;
  password?: string;
  deliveryChannel?: string;
  resend?: boolean;
  forceReissue?: boolean;
  defaultCountry?: string;
}) {
  return authPost<AuthOtpResponse>("/api/auth/identity/change/request", body, {
    activitySource: "user_initiated",
  });
}

export function confirmIdentityChange(body: {
  email?: string;
  phone?: string;
  code: string;
  password?: string;
  defaultCountry?: string;
}) {
  return authPost<{ user?: AuthUser }>("/api/auth/identity/change/confirm", body, {
    activitySource: "user_initiated",
  });
}

export async function requestPasswordChange(
  body: {
    deliveryChannel?: "email" | "sms" | "whatsapp" | "voice";
    resend?: boolean;
    forceReissue?: boolean;
    challengeId?: string;
    completionSecret?: string;
  },
  expected: {
    identityType: "email" | "phone";
    deliveryChannel: "email" | "sms" | "whatsapp" | "voice";
  },
) {
  const response = await authPost<unknown>("/api/auth/password/change/request", body, {
    activitySource: "user_initiated",
  });
  return readPasswordChangeChallenge(response, expected);
}

export async function pollPasswordChangeApproval(body: {
  challengeId: string;
  completionSecret: string;
}) {
  const response = await authPost<unknown>("/api/auth/password/change/status", body, {
    activitySource: getAutomaticActivitySource(),
  });
  return readPasswordChangeApprovalStatus(response);
}

export async function confirmPasswordChange(
  body: {
    challengeId: string;
    completionSecret: string;
    code?: string;
    newPassword: string;
  },
  expectedUserId: string,
  expectedVerificationIdentityType: AuthUser["verificationIdentityType"],
) {
  const response = await authPost<unknown>("/api/auth/password/change/confirm", body, {
    activitySource: "user_initiated",
  });
  return readPasswordChangeConfirmation(
    response,
    expectedUserId,
    expectedVerificationIdentityType,
  ) as AuthUser;
}

export function listAuthSessions() {
  return authGet<{ sessions?: AuthSessionInfo[] }>("/api/auth/sessions");
}

export async function logout(allSessions = false) {
  const permit = beginAuthSessionMutation(allSessions ? "logout-all" : "logout");
  try {
    await authPost("/api/auth/logout", { allSessions }, { activitySource: "user_initiated" });
  } catch {
    /* ignore */
  }
  if (!isAuthSessionMutationCurrent(permit)) return;
  clearAuthSessionStorage();
  cancelAuthSessionMutation(permit);
}

export function signOutAllSessions() {
  return logout(true);
}

export async function deleteCurrentAccount(
  confirmPhrase: string,
): Promise<AccountDeletionResponse & { sessionCleared: boolean }> {
  if (!canConfirmAccountDeletion(confirmPhrase)) {
    throw normalizeApiError(
      Object.assign(new Error("confirm_required"), { code: "confirm_required" }),
      "confirm_required",
    );
  }
  const permit = beginAuthSessionMutation("account-delete");
  const owner = captureAuthSessionOwner();
  if (!owner) {
    cancelAuthSessionMutation(permit);
    throw normalizeApiError(
      Object.assign(new Error("auth_required"), { code: "auth_required" }),
      "auth_required",
    );
  }
  const accountScope = accountStorageScopeForStableId(owner.userId);
  const legacySessionScope = accountStorageScopeForStableId(owner.sessionId);
  try {
    const response = readAccountDeletionResponse(
      await authPost<unknown>(
        "/api/auth/account/delete",
        {
          confirmPhrase: confirmPhrase.trim(),
        },
        { activitySource: "user_initiated" },
      ),
    );

    // The server deletion is now permanent. Clear every browser row belonging
    // to the deleted account even if another auth transition won the race.
    for (const storage of availableBrowserStorages()) {
      for (const scope of new Set([accountScope, legacySessionScope])) {
        if (scope) purgeAccountStorageScope(storage, scope);
      }
    }

    if (accountScope) {
      // The backend deletion succeeded. Purge only its captured recording owner,
      // even if a different session won the account transition in the meantime.
      await import("@/shared/audio/retained-audio-store")
        .then(({ deleteRetainedAudioForScope }) => deleteRetainedAudioForScope(accountScope))
        .catch(() => { /* Browser storage can be unavailable; the original seven-day expiry still applies. */ });
    }

    const sessionCleared = isAuthSessionMutationCurrent(permit) && isAuthSessionOwnerCurrent(owner);
    if (sessionCleared) clearAuthSessionStorage();
    cancelAuthSessionMutation(permit);
    return { ...response, sessionCleared };
  } catch (error) {
    cancelAuthSessionMutation(permit);
    throw error;
  }
}

export function formatAuthError(err: unknown): string {
  const e = normalizeApiError(err);
  const code = e.code || "";
  if (code === "invalid_credentials") {
    return "Your email, mobile number, or password is incorrect.";
  }
  if (code === "account_locked") {
    return "Too many unsuccessful attempts. Try again in 15 minutes or reset your password.";
  }
  if (code === "missing_id_token" || code === "invalid_google_token") {
    return "Google sign-in could not be verified. Please try again.";
  }
  if (code === "google_not_configured") return "Google sign-in is not configured for this app.";
  if (code === "google_email_required") {
    return "Choose a Google account with a verified email address.";
  }
  if (code === "email_taken") {
    return "An account already exists for this email. Sign in to continue verification.";
  }
  if (code === "phone_taken") {
    return "An account already exists for this mobile number. Sign in to continue verification.";
  }
  if (code === "current_contact_verification_required") {
    return "This contact is already on your account but still needs verification. Refresh Profile and verify it before trying again.";
  }
  if (code === "invalid_identity_change_response") {
    return "We could not safely confirm that verification method. Send a fresh request and try again.";
  }
  if (code === "invalid_current_contact_verification_response") {
    return "We could not safely confirm that contact verification. Send a fresh request and try again.";
  }
  if (code === "identity_change_user_mismatch") {
    return "Your account or contact changed. Return to Profile and request a fresh verification.";
  }
  if (code === "device_bound_to_other_account") {
    const body = e.body && typeof e.body === "object" ? e.body : {};
    const masked =
      "deviceOwnerMasked" in body && typeof body.deviceOwnerMasked === "string"
        ? body.deviceOwnerMasked
        : "another account";
    return `This browser is linked to ${masked}. Sign out there first, or continue on that device.`;
  }
  if (code === "invalid_otp") return "That code is incorrect or expired.";
  if (code === "otp_target_mismatch") return "That code was issued for a different account.";
  if (
    code === "invalid_contact_sign_in_challenge" ||
    code === "contact_sign_in_challenge_expired"
  ) {
    return "This verification request has expired. Sign in again to request a new one.";
  }
  if (code === "credential_version_mismatch") {
    return "Your sign-in details changed after this request started. Sign in again.";
  }
  if (code === "verification_in_progress") {
    return "Verification is already completing. Wait a moment, then try again.";
  }
  if (code === "contact_sign_in_device_mismatch") {
    return "For your security, complete verification in the browser where you started signing in.";
  }
  if (code === "contact_already_verified") {
    return "This email or mobile number is already verified. Return to sign in and try again.";
  }
  if (code === "unsupported_delivery_channel") {
    return "That verification method is not available for this account contact.";
  }
  if (code === "invalid_contact_sign_in_response") {
    return "We couldn't securely continue this sign-in. Please start again.";
  }
  if (code === "contact_sign_in_challenge_write_failed") {
    return "We couldn't securely start verification. Please try signing in again.";
  }
  if (code === "invalid_auth_token_response") {
    return "We couldn't securely complete this sign-in. Please start again.";
  }
  if (code === "invalid_auth_session_response") {
    return "We couldn't securely validate this session. Please sign in again.";
  }
  if (code === "confirm_required") return "Type DELETE to confirm account deletion.";
  if (code === "user_not_found") return "This account no longer exists.";
  if (code === "invalid_account_deletion_response") {
    return "We could not safely confirm account deletion. Please try again.";
  }
  if (code === "auth_session_mutation_superseded") {
    return "Your account session changed. Refresh and try again.";
  }
  if (code === "rate_limited") return "Too many attempts. Please wait a few minutes.";
  if (code === "authentication_state_write_failed") {
    return "We couldn’t securely record this attempt. Please try again shortly.";
  }
  if (e?.message && e.message !== "request_failed") return e.message;
  return "We couldn't complete that sign-in step. Check your connection and try again.";
}
